import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError, concatMap, map, toArray } from 'rxjs/operators';
import { Culture, StatutCulture } from 'src/app/core/models/culture.model';
import { CultureService } from 'src/app/core/services/entities/culture.service';
import { RecolteService } from 'src/app/core/services/entities/recolte.service';
import { ChampCommun, ColonneGroupee, LigneGroupee, TraitementGroupe } from './saisie-groupee.component';

type Onglet = 'recolte' | 'statut' | 'supprimer';
interface EtatLigne { statut: 'ok' | 'erreur'; message?: string; }

/**
 * Actions groupées sur plusieurs cultures : récolte (quantité par culture, sans doublon au nouvel essai),
 * changement de statut, suppression (administrateur, confirmation par saisie du nombre).
 * Les écritures partent l'une après l'autre ; un nouvel essai ne reprend que les lignes en erreur.
 */
@Component({
  selector: 'bay-actions-cultures',
  template: `
    <div class="d-flex gap-2 mb-3 flex-wrap" role="tablist">
      <button type="button" class="bay-btn bay-btn-sm" [class.bay-btn-primary]="onglet === 'recolte'" [class.bay-btn-ghost]="onglet !== 'recolte'"
              (click)="choisir('recolte')" data-onglet="recolte">Récolte</button>
      <button type="button" class="bay-btn bay-btn-sm" [class.bay-btn-primary]="onglet === 'statut'" [class.bay-btn-ghost]="onglet !== 'statut'"
              (click)="choisir('statut')" data-onglet="statut">Changer le statut</button>
      <button *ngIf="peutSupprimer" type="button" class="bay-btn bay-btn-sm" [class.bay-btn-primary]="onglet === 'supprimer'" [class.bay-btn-ghost]="onglet !== 'supprimer'"
              (click)="choisir('supprimer')" data-onglet="supprimer">Supprimer</button>
    </div>

    <ng-container *ngIf="onglet === 'recolte'">
      <label class="d-flex align-items-center gap-2 mb-2" style="font-size:14px;">
        <input type="checkbox" class="form-check-input m-0" [(ngModel)]="terminer" data-test="terminer">
        Récolte finale : marquer ces cultures « Terminée »
      </label>
      <bay-saisie-groupee [lignes]="lignes" [colonnes]="colonnes" [champsCommuns]="champs" libelleLigne="Culture"
        [traitement]="traitementRecolte" (fermer)="fermer.emit($event || modifie)"></bay-saisie-groupee>
    </ng-container>

    <ng-container *ngIf="onglet !== 'recolte'">
      <div *ngIf="onglet === 'statut'" class="mb-3">
        <label class="bay-label" for="ac-statut">Nouveau statut</label>
        <select id="ac-statut" class="bay-input" [(ngModel)]="statut" [disabled]="envoi">
          <option *ngFor="let s of statuts" [ngValue]="s.value">{{ s.label }}</option>
        </select>
      </div>
      <div *ngIf="onglet === 'supprimer'" class="mb-3" data-etat="confirmation">
        <p class="mb-2" style="font-size:14px;color:#991b1b;">
          Les {{ lignes.length }} culture(s) ci-dessous et leurs photos seront supprimées définitivement. Les récoltes et ventes déjà enregistrées restent.
        </p>
        <label class="bay-label" for="ac-confirmer">Tapez {{ lignes.length }} pour confirmer</label>
        <input id="ac-confirmer" class="bay-input" inputmode="numeric" [(ngModel)]="confirmation" [disabled]="envoi">
      </div>
      <ul class="list-unstyled mb-3" style="max-height:40vh;overflow:auto;">
        <li *ngFor="let l of lignes" class="d-flex justify-content-between py-1 border-bottom" [attr.data-ligne]="l.id" style="font-size:14px;">
          <span>{{ l.nom }} <span class="text-muted">{{ l.info }}</span></span>
          <span *ngIf="etat[l.id]?.statut === 'ok'" class="text-success">✓</span>
          <span *ngIf="etat[l.id]?.statut === 'erreur'" class="text-danger" style="font-size:12px;">{{ etat[l.id]?.message }}</span>
        </li>
      </ul>
      <div *ngIf="erreur" class="text-danger mb-2" style="font-size:13px;" data-etat="erreur">{{ erreur }}</div>
      <div *ngIf="bilan" class="mb-2" style="font-size:13px;" data-etat="bilan">{{ bilan }}</div>
      <div class="d-flex gap-2">
        <button type="button" class="bay-btn bay-btn-sm" [class.bay-btn-primary]="onglet === 'statut'" [class.bay-btn-danger]="onglet === 'supprimer'"
                [disabled]="envoi || restantes.length === 0" (click)="appliquer()">
          {{ aDesErreurs ? 'Réessayer les lignes en erreur' : onglet === 'statut' ? 'Appliquer' : 'Supprimer' }}
        </button>
        <button type="button" class="bay-btn bay-btn-ghost bay-btn-sm" [disabled]="envoi" (click)="fermer.emit(modifie)">
          {{ modifie ? 'Terminer' : 'Annuler' }}</button>
      </div>
    </ng-container>
  `
})
export class ActionsCulturesComponent implements OnChanges {
  @Input() cultures: Culture[] = [];
  @Input() fermeId!: number;
  @Input() peutSupprimer = false;
  @Input() statuts: { label: string; value: StatutCulture }[] = [
    { label: 'En préparation', value: 'EN_PREPARATION' }, { label: 'En cours', value: 'EN_COURS' }, { label: 'Terminée', value: 'TERMINEE' }];
  /** true : au moins une écriture a réussi (la liste est à recharger) */
  @Output() fermer = new EventEmitter<boolean>();

  onglet: Onglet = 'recolte';
  lignes: LigneGroupee[] = [];
  readonly colonnes: ColonneGroupee[] = [{ cle: 'kg', libelle: 'Quantité', unite: 'kg' }];
  readonly champs: ChampCommun[] = [
    { cle: 'date', libelle: 'Date de récolte', type: 'date', obligatoire: true },
    { cle: 'note', libelle: 'Note', type: 'texte', placeholder: 'facultatif' }];
  terminer = false;

  statut: StatutCulture = 'EN_COURS';
  confirmation = '';
  etat: Record<number, EtatLigne> = {};
  envoi = false;
  erreur = '';
  bilan = '';
  modifie = false;

  constructor(private cultureService: CultureService, private recolteService: RecolteService) {}

  ngOnChanges(): void {
    this.lignes = this.cultures.map(c => ({ id: c.id, nom: c.nom + (c.variete ? ' ' + c.variete : ''),
      info: c.surface ? c.surface : undefined }));
    this.etat = {};
    this.bilan = '';
    this.erreur = '';
  }

  choisir(o: Onglet): void {
    if (this.envoi) { return; }
    this.onglet = o;
    this.etat = {};
    this.bilan = '';
    this.erreur = '';
    this.confirmation = '';
  }

  readonly traitementRecolte: TraitementGroupe = (l, v, communs, ref) => {
    if (!v.kg) { return []; }
    const culture = this.cultures.find(c => c.id === l.id);
    this.modifie = true;
    return [this.recolteService.create({
      cultureId: l.id, fermeId: this.fermeId, zoneId: culture?.zoneId ?? null, dateRecolte: communs.date,
      quantiteKg: v.kg, note: communs.note || undefined, clientRef: ref('kg'), terminerCulture: this.terminer || undefined
    })];
  };

  /** Lignes à (re)traiter : toutes au premier passage, puis seulement celles en erreur */
  get restantes(): LigneGroupee[] { return this.lignes.filter(l => this.etat[l.id]?.statut !== 'ok'); }
  get aDesErreurs(): boolean { return this.lignes.some(l => this.etat[l.id]?.statut === 'erreur'); }

  appliquer(): void {
    this.erreur = '';
    if (this.onglet === 'supprimer' && this.confirmation.trim() !== String(this.lignes.length)) {
      this.erreur = `Tapez ${this.lignes.length} pour confirmer la suppression.`;
      return;
    }
    const action = (l: LigneGroupee): Observable<unknown> => this.onglet === 'statut'
      ? this.cultureService.changerStatut(l.id, this.statut) : this.cultureService.delete(l.id);
    this.envoi = true;
    from(this.restantes).pipe(
      concatMap(l => action(l).pipe(
        map(() => ({ id: l.id, ok: true, message: '' })),
        catchError(err => of({ id: l.id, ok: false, message: err?.error?.message || (err?.status === 0 ? 'Pas de connexion' : 'Échec') })))),
      toArray()
    ).subscribe(res => {
      for (const r of res) {
        this.etat[r.id] = r.ok ? { statut: 'ok' } : { statut: 'erreur', message: r.message };
        if (r.ok) { this.modifie = true; }
      }
      const ok = this.lignes.filter(l => this.etat[l.id]?.statut === 'ok').length;
      const ko = this.lignes.length - ok;
      this.bilan = `${ok} culture(s) ${this.onglet === 'statut' ? 'mise(s) à jour' : 'supprimée(s)'}` + (ko ? `, ${ko} en erreur` : '');
      this.envoi = false;
    });
  }
}
